import { sectionRenderer, morphSection } from '@theme/section-renderer';

const ERA_PARAM = 'filter.p.m.elohm.era';
const ERA_CONTEXT = {
  what_is: 'The present ELOHM language: construction, silhouette, and lasting identity.',
  what_was: 'Earlier chapters preserved as an intentional, purchasable archive.',
};
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const ERA_INTERACTED_KEY = 'elohm-era-interacted-v1';
const ERA_NUDGE_IDLE_MS = 4000;
let navigationSequence = 0;
let idleNudgeTimer = 0;
let idleNudgeObserver = null;
let idleNudgeVisible = false;
let idleNudgePlayed = false;
let idleNudgeCleanup = () => {};

function sessionFlag(key) {
  try {
    return window.sessionStorage.getItem(key) === 'true';
  } catch (error) {
    return false;
  }
}

function setSessionFlag(key) {
  try {
    window.sessionStorage.setItem(key, 'true');
  } catch (error) {
    // Session storage is optional; the in-memory guard still prevents repetition on this page.
  }
}

function clearIdleNudge() {
  window.clearTimeout(idleNudgeTimer);
  idleNudgeTimer = 0;
  document.querySelectorAll('[data-elohm-era-link].is-idle-nudge').forEach((link) => {
    link.classList.remove('is-idle-nudge');
  });
}

function suppressIdleNudge() {
  idleNudgePlayed = true;
  setSessionFlag(ERA_INTERACTED_KEY);
  clearIdleNudge();
  idleNudgeCleanup();
}

function setupIdleEraNudge() {
  const navigation = document.querySelector('[data-elohm-era-navigation]');
  if (
    !navigation ||
    REDUCED_MOTION.matches ||
    sessionFlag(ERA_INTERACTED_KEY)
  ) {
    return;
  }

  const inactiveLink = navigation.querySelector('[data-elohm-era-link]:not(.is-active)');
  if (!(inactiveLink instanceof HTMLAnchorElement)) return;

  inactiveLink.style.setProperty(
    '--elohm-era-nudge-x',
    inactiveLink.dataset.elohmEraLink === 'what_was' ? '-2px' : '2px'
  );

  let controlsEngaged = false;
  const activityEvents = ['pointermove', 'pointerdown', 'touchstart', 'keydown', 'scroll'];

  const cleanup = () => {
    clearIdleNudge();
    idleNudgeObserver?.disconnect();
    idleNudgeObserver = null;
    for (const eventName of activityEvents) {
      window.removeEventListener(eventName, onActivity, true);
    }
    navigation.removeEventListener('pointerenter', onControlsEnter);
    navigation.removeEventListener('pointerleave', onControlsLeave);
    navigation.removeEventListener('focusin', onControlsEnter);
    navigation.removeEventListener('focusout', onControlsLeave);
  };

  const playNudge = () => {
    if (
      idleNudgePlayed ||
      !idleNudgeVisible ||
      controlsEngaged ||
      document.hidden ||
      sessionFlag(ERA_INTERACTED_KEY)
    ) {
      return;
    }

    idleNudgePlayed = true;
    cleanup();
    inactiveLink.classList.add('is-idle-nudge');
    window.setTimeout(() => inactiveLink.classList.remove('is-idle-nudge'), 1150);
  };

  const schedule = () => {
    clearIdleNudge();
    if (!idleNudgeVisible || controlsEngaged || idleNudgePlayed) return;
    idleNudgeTimer = window.setTimeout(playNudge, ERA_NUDGE_IDLE_MS);
  };

  function onActivity() {
    if (idleNudgePlayed) return;
    clearIdleNudge();
    schedule();
  }

  function onControlsEnter() {
    controlsEngaged = true;
    clearIdleNudge();
  }

  function onControlsLeave() {
    controlsEngaged = false;
    schedule();
  }

  idleNudgeCleanup = cleanup;

  for (const eventName of activityEvents) {
    window.addEventListener(eventName, onActivity, { capture: true, passive: true });
  }
  navigation.addEventListener('pointerenter', onControlsEnter);
  navigation.addEventListener('pointerleave', onControlsLeave);
  navigation.addEventListener('focusin', onControlsEnter);
  navigation.addEventListener('focusout', onControlsLeave);

  if ('IntersectionObserver' in window) {
    idleNudgeObserver = new IntersectionObserver(
      ([entry]) => {
        idleNudgeVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.5);
        if (idleNudgeVisible) {
          schedule();
        } else {
          clearIdleNudge();
        }
      },
      { threshold: [0, 0.5, 1] }
    );
    idleNudgeObserver.observe(navigation);
  } else {
    idleNudgeVisible = true;
    schedule();
  }
}

const wait = (duration) => new Promise((resolve) => window.setTimeout(resolve, duration));

function sectionRoot(sectionId) {
  return document.getElementById(`shopify-section-${sectionId}`);
}

function targetUrlFor(link) {
  const target = new URL(window.location.href);
  const fallback = new URL(link.href, window.location.href);

  target.pathname = fallback.pathname;
  target.searchParams.set(ERA_PARAM, link.dataset.elohmEraLink);
  target.searchParams.delete('page');
  target.searchParams.delete('section_id');
  target.hash = '';

  return target;
}

function eraFromUrl(url) {
  const era = url.searchParams.get(ERA_PARAM);
  return era === 'what_is' || era === 'what_was' ? era : '';
}

function setNavigationState(root, era) {
  const navigation = root.querySelector('[data-elohm-era-navigation]');
  if (!navigation) return;

  navigation.dataset.activeEra = era;

  for (const link of navigation.querySelectorAll('[data-elohm-era-link]')) {
    const isActive = link.dataset.elohmEraLink === era;
    link.classList.toggle('is-active', isActive);

    if (isActive) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  }

  const context = navigation.querySelector('[data-elohm-era-context]');
  if (context) {
    context.textContent =
      ERA_CONTEXT[era] || 'The full catalog remains visible while product eras are being classified.';
  }
}

function updateVisibleCount(root, count) {
  const itemLabel = `${count} ${count === 1 ? 'item' : 'items'}`;

  for (const countLabel of root.querySelectorAll('.products-count-wrapper span')) {
    countLabel.textContent = itemLabel;
  }

  for (const resultsButton of root.querySelectorAll('.facets__see-results')) {
    resultsButton.textContent = `See ${itemLabel}`;
  }
}

function hasOtherProductFilters(url) {
  for (const key of url.searchParams.keys()) {
    if (key.startsWith('filter.') && key !== ERA_PARAM) return true;
  }

  return false;
}

function syncEraEmptyState(root, era, visibleCount, targetUrl) {
  const emptyState = root.querySelector('[data-elohm-era-empty-state]');
  const collectionContent = root.querySelector('[data-elohm-era-collection-content]');
  if (!emptyState || !collectionContent) return;

  const shouldShow =
    era === 'what_is' &&
    visibleCount === 0 &&
    !hasOtherProductFilters(targetUrl);

  emptyState.hidden = !shouldShow;
  collectionContent.hidden = shouldShow;
}

function applyClientEraFallback(root, targetUrl) {
  const navigation = root.querySelector('[data-elohm-era-navigation]');
  if (!navigation) return;

  const era = eraFromUrl(targetUrl);
  const cards = [...root.querySelectorAll('[data-elohm-era-product]')];
  const results = root.querySelector('[data-elohm-era-results]');
  let visibleCount = Number.parseInt(results?.dataset.elohmResultsCount || '', 10);

  if (!Number.isFinite(visibleCount)) {
    visibleCount = cards.length;
  }

  if (navigation.dataset.filterAvailable !== 'true') {
    for (const card of cards) {
      card.hidden = Boolean(era) && card.dataset.elohmEra !== era;
    }

    visibleCount = cards.filter((card) => !card.hidden).length;
    setNavigationState(root, era);
    updateVisibleCount(root, visibleCount);
    root.dataset.elohmEraClientFiltered = Boolean(era).toString();
  }

  syncEraEmptyState(root, era, visibleCount, targetUrl);
}

async function renderEraState(targetUrl, sectionId, updateHistory) {
  const sequence = ++navigationSequence;
  const currentRoot = sectionRoot(sectionId);
  const cleanTargetUrl = new URL(targetUrl.toString());
  cleanTargetUrl.searchParams.delete('section_id');

  if (!currentRoot) {
    window.location.assign(cleanTargetUrl);
    return;
  }

  currentRoot.setAttribute('aria-busy', 'true');

  try {
    if (!REDUCED_MOTION.matches) {
      currentRoot.classList.add('elohm-era-transition-out');
      await wait(240);
    }

    const html = await sectionRenderer.getSectionHTML(sectionId, false, new URL(cleanTargetUrl.toString()));
    if (sequence !== navigationSequence) return;

    await morphSection(sectionId, html);

    if (updateHistory) {
      window.history.pushState({ elohmEra: true }, '', cleanTargetUrl);
    }

    const incomingRoot = sectionRoot(sectionId);
    if (!incomingRoot) return;

    applyClientEraFallback(incomingRoot, cleanTargetUrl);
    incomingRoot.classList.remove('elohm-era-transition-out');
    incomingRoot.removeAttribute('aria-busy');

    if (!REDUCED_MOTION.matches) {
      incomingRoot.classList.add('elohm-era-transition-in');
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => incomingRoot.classList.remove('elohm-era-transition-in'));
      });
    }

    if (updateHistory) {
      incomingRoot.querySelector('[data-elohm-era-link][aria-current="page"]')?.focus({ preventScroll: true });
    }
  } catch (error) {
    if (sequence !== navigationSequence) return;
    window.location.assign(cleanTargetUrl);
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const link = event.target.closest('[data-elohm-era-link]');
  if (!(link instanceof HTMLAnchorElement)) return;
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  suppressIdleNudge();

  const navigation =
    link.closest('[data-elohm-era-navigation]') ||
    document.querySelector('[data-elohm-era-navigation]');
  const sectionId = navigation?.dataset.sectionId;
  if (!sectionId) return;

  event.preventDefault();
  const targetUrl = targetUrlFor(link);

  if (targetUrl.toString() === window.location.href) return;
  renderEraState(targetUrl, sectionId, true);
});

window.addEventListener('popstate', () => {
  const navigation = document.querySelector('[data-elohm-era-navigation]');
  const sectionId = navigation?.dataset.sectionId;
  if (!sectionId) return;

  renderEraState(new URL(window.location.href), sectionId, false);
});

function applyApprovedDefault() {
  const navigation = document.querySelector('[data-elohm-era-navigation]');
  const currentUrl = new URL(window.location.href);
  if (!navigation || currentUrl.searchParams.has(ERA_PARAM)) return false;

  let defaultEra = navigation.dataset.defaultEra;
  if (defaultEra !== 'what_is' && defaultEra !== 'what_was') return false;

  if (navigation.dataset.filterAvailable !== 'true') {
    const cards = [...document.querySelectorAll('[data-elohm-era-product]')];

    if (cards.length > 0 && cards.every((card) => card.dataset.elohmEra === 'what_was')) {
      defaultEra = 'what_was';
    } else if (cards.some((card) => card.dataset.elohmEra === 'what_is')) {
      defaultEra = 'what_is';
    }
  }

  const link = navigation.querySelector(`[data-elohm-era-link="${defaultEra}"]`);
  if (!(link instanceof HTMLAnchorElement)) return false;

  window.location.replace(targetUrlFor(link));
  return true;
}

const defaultRedirectStarted = applyApprovedDefault();
if (!defaultRedirectStarted) {
  setupIdleEraNudge();
}

const initialNavigation = document.querySelector('[data-elohm-era-navigation]');
const initialSectionId = initialNavigation?.dataset.sectionId;
const initialRoot = initialSectionId ? sectionRoot(initialSectionId) : null;
if (initialRoot && !defaultRedirectStarted) {
  applyClientEraFallback(initialRoot, new URL(window.location.href));
}
